import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { MirrorService } from 'services/MirrorService';

import { useCreditsAnalytics } from 'hooks/analytics/useCreditsAnalytics';
import { getCreditsCurrencySelector } from 'store/payment/selectors';

import { ReactComponent as HeartIcon } from 'assets/icons/heart.svg';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { ImagedPopup } from 'components/UI/ImagedPopup';

import thumbnailImg from './images/thumbnail.jpg';
import thumbnailMePlusYouImg from './images/thumbnail_meplusyou.jpg';

import css from './creditsPurcasedPopup.module.sass';

interface Props {
  onClose: () => void;
  params: {
    creditsAmount: number;
    orderId: string;
    successEvent?: {
      name: string;
      data?: any;
    };
  };
}

export const CreditsPurcasedPopup: React.FC<Props> = ({ params, onClose }) => {
  const { t } = useTranslation(['common', 'app']);

  const creditsCurrency = useSelector(getCreditsCurrencySelector);

  const { trackVerticaCreditsPurchaseSuccess } = useCreditsAnalytics();

  const handleClose = () => {
    trackVerticaCreditsPurchaseSuccess({
      orderId: params.orderId,
      cost: params.creditsAmount,
      operation: 'close',
    });

    if (params.successEvent) {
      const successEvent = new CustomEvent(params.successEvent.name, {
        detail: params.successEvent?.data,
      });
      document.dispatchEvent(successEvent);
    }

    onClose();
  };

  const handleContinueClick = () => {
    trackVerticaCreditsPurchaseSuccess({
      orderId: params.orderId,
      cost: params.creditsAmount,
      operation: 'click',
    });

    if (params.successEvent) {
      const successEvent = new CustomEvent(params.successEvent.name, {
        detail: params.successEvent?.data,
      });
      document.dispatchEvent(successEvent);
    }

    onClose();
  };

  useEffect(() => {
    trackVerticaCreditsPurchaseSuccess({
      orderId: params.orderId,
      cost: params.creditsAmount,
      operation: 'show',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ImagedPopup
      image={
        MirrorService.isMePlusYouOrLocalsjoy
          ? thumbnailMePlusYouImg
          : thumbnailImg
      }
      icon={<HeartIcon />}
      onClose={handleClose}
    >
      <p className={css.title}>
        {t('app:creditsPurcasedPopup.title')}
        <span className={css.credits}>
          {' '}
          {params?.creditsAmount} {creditsCurrency}
        </span>
      </p>
      <BaseButton
        type={ButtonTypes.Accent}
        className={css.button}
        onClick={handleContinueClick}
      >
        {t('common:continue')}
      </BaseButton>
      {!!MirrorService.supportPhone && (
        <p className={css.supportPhone}>{MirrorService.supportPhone}</p>
      )}
    </ImagedPopup>
  );
};
